<template>
    <div
        class="CommentWrap"
        :class="[
            { 'is-disabled': isDisabled },
            { 'has-error': error && error.length > 0 },
            `is-${direction}`,
        ]"
        :style="style"
    >
        <div v-if="showAddForm" class="CommentForm">
            <textarea
                ref="commentTextarea"
                class="CommentInput"
                v-model="newCommentText"
                :placeholder="placeholder"
                :disabled="isDisabled"
                rows="3"
                @keyup.ctrl.enter="handleSaveComment"
            ></textarea>
            <div class="CommentActions">
                <Button variant="plain" size="small" @onClick="handleCancelComment">
                    Abbrechen
                </Button>
                <Button
                    size="small"
                    :is-disabled="!newCommentText.trim() || isSubmitting"
                    :is-loading="isSubmitting"
                    @onClick="handleSaveComment"
                >
                    {{ editingComment ? 'Aktualisieren' : 'Hinzufügen' }}
                </Button>
            </div>
        </div>

        <div v-if="isLoading" class="LoadingState">
            <div class="LoadingSpinner">Laden...</div>
        </div>

        <div v-else-if="comments.length > 0" class="CommentsList">
            <div
                v-for="comment in comments"
                :key="comment.id"
                class="CommentItem"
                :class="{ 'is-editing': editingComment && editingComment.id === comment.id }"
            >
                <div class="CommentHeader">
                    <span class="CommentDate">{{ formatDate(comment.createdAt) }}</span>
                    <div v-if="!isDisabled" class="CommentControls">
                        <Button
                            variant="icon"
                            size="small"
                            @onClick="() => handleEditComment(comment)"
                        >
                            <i class="ri-edit-line" style="font-size: 1rem"></i>
                        </Button>
                        <Button
                            variant="icon"
                            size="small"
                            @onClick="() => handleDeleteComment(comment)"
                        >
                            <i class="ri-delete-bin-line" style="font-size: 1rem"></i>
                        </Button>
                    </div>
                </div>
                <div class="CommentContent">{{ comment.message }}</div>
                <div
                    v-if="comment.updatedAt && comment.updatedAt !== comment.createdAt"
                    class="CommentUpdated"
                >
                    Zuletzt aktualisiert: {{ formatDate(comment.updatedAt) }}
                </div>
            </div>
        </div>

        <div v-else-if="comments.length === 0 && !showAddForm && !isLoading" class="EmptyState">
            <span class="EmptyStateText">Noch keine Kommentare</span>
        </div>

        <div v-if="!showAddForm && !isDisabled" class="AddCommentButton">
            <Button size="small" @onClick="showAddCommentForm">
                <template #iconBefore>
                    <i class="ri-add-line"></i>
                </template>
                Kommentar hinzufügen
            </Button>
        </div>

        <span v-if="error && error.length > 0" class="Error">{{ error }}</span>
    </div>
</template>

<script>
import axios from 'axios';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import Button from '@/components/widgets/Button.vue';

export default {
    name: 'Comment',
    components: {
        Button,
    },
    props: {
        label: {
            type: String,
            default: 'Kommentare',
        },
        resourceType: {
            type: String,
            required: true,
        },
        resourceUuid: {
            type: String,
            required: true,
        },
        isDisabled: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: 'Kommentar schreiben...',
        },
        error: {
            type: String,
            default: '',
        },
        direction: {
            type: String,
            default: 'vertical',
        },
        marginBottom: {
            type: Number,
            default: 0,
        },
        marginTop: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            comments: [],
            showAddForm: false,
            newCommentText: '',
            editingComment: null,
            isLoading: false,
            isSubmitting: false,
        };
    },
    computed: {
        style() {
            return {
                marginBottom: `${this.marginBottom * 5}px`,
                marginTop: `${this.marginTop * 5}px`,
            };
        },
    },
    async mounted() {
        await this.fetchComments();
    },
    watch: {
        resourceUuid: {
            handler() {
                if (this.resourceUuid) {
                    this.fetchComments();
                }
            },
            immediate: false,
        },
    },
    methods: {
        async fetchComments() {
            if (!this.resourceUuid || !this.resourceType) return;

            this.isLoading = true;
            try {
                const url = `${import.meta.env.VITE_API_BASE_URL}/cpanel/${this.resourceType}/${this.resourceUuid}/comments`;
                const response = await axios.get(url, { withCredentials: true });
                this.comments = response.data || [];
            } catch (error) {
                console.error('Error fetching comments:', error);
                this.$toasted?.show('Fehler beim Laden der Kommentare', { type: 'error' });
                this.comments = [];
            } finally {
                this.isLoading = false;
            }
        },
        async createComment(commentData) {
            try {
                const url = `${import.meta.env.VITE_API_BASE_URL}/cpanel/${this.resourceType}/${this.resourceUuid}/comments`;
                const response = await axios.post(url, commentData, { withCredentials: true });
                return response.data;
            } catch (error) {
                console.error('Error creating comment:', error);
                this.$toasted?.show('Fehler beim Erstellen des Kommentars', { type: 'error' });
                throw error;
            }
        },
        async updateComment(commentUuid, commentData) {
            try {
                const url = `${import.meta.env.VITE_API_BASE_URL}/cpanel/${this.resourceType}/${this.resourceUuid}/comments/${commentUuid}`;
                const response = await axios.put(url, commentData, { withCredentials: true });
                return response.data;
            } catch (error) {
                console.error('Error updating comment:', error);
                this.$toasted?.show('Fehler beim Aktualisieren des Kommentars', {
                    type: 'error',
                });
                throw error;
            }
        },
        async deleteComment(commentUuid) {
            try {
                const url = `${import.meta.env.VITE_API_BASE_URL}/cpanel/${this.resourceType}/${this.resourceUuid}/comments/${commentUuid}`;
                await axios.delete(url, { withCredentials: true });
            } catch (error) {
                console.error('Error deleting comment:', error);
                this.$toasted?.show('Fehler beim Löschen des Kommentars', { type: 'error' });
                throw error;
            }
        },
        showAddCommentForm() {
            this.showAddForm = true;
            this.newCommentText = '';
            this.editingComment = null;
            this.$nextTick(() => {
                this.$refs.commentTextarea?.focus();
            });
        },
        handleEditComment(comment) {
            this.editingComment = comment;
            this.newCommentText = comment.message;
            this.showAddForm = true;
            this.$nextTick(() => {
                this.$refs.commentTextarea?.focus();
            });
        },
        async handleSaveComment() {
            if (!this.newCommentText.trim() || this.isSubmitting) return;

            this.isSubmitting = true;
            try {
                const commentData = {
                    message: this.newCommentText.trim(),
                };

                if (this.editingComment) {
                    // Update existing comment
                    const updatedComment = await this.updateComment(
                        this.editingComment.uuid || this.editingComment.id,
                        commentData,
                    );
                    const index = this.comments.findIndex(
                        (c) =>
                            (c.uuid || c.id) ===
                            (this.editingComment.uuid || this.editingComment.id),
                    );
                    if (index !== -1) {
                        this.comments.splice(index, 1, updatedComment);
                    }
                    this.$toasted?.show('Kommentar erfolgreich aktualisiert', { type: 'success' });
                } else {
                    // Add new comment
                    const newComment = await this.createComment(commentData);
                    this.comments.push(newComment);
                    this.$toasted?.show('Kommentar erfolgreich hinzugefügt', { type: 'success' });
                }

                this.handleCancelComment();
            } catch (error) {
                console.error('Error saving comment:', error);
                this.$toasted?.show('Bitte versuchen Sie es erneut.', { type: 'error' });
                throw error;
            } finally {
                this.isSubmitting = false;
            }
        },
        handleCancelComment() {
            this.showAddForm = false;
            this.newCommentText = '';
            this.editingComment = null;
        },
        async handleDeleteComment(comment) {
            if (!confirm('Sind Sie sicher, dass Sie diesen Kommentar löschen möchten?')) {
                return;
            }

            try {
                await this.deleteComment(comment.uuid || comment.id);
                this.comments = this.comments.filter(
                    (c) => (c.uuid || c.id) !== (comment.uuid || comment.id),
                );
                this.$toasted?.show('Kommentar erfolgreich gelöscht', { type: 'success' });
            } catch (error) {
                console.error('Error deleting comment:', error);
                this.$toasted?.show('Bitte versuchen Sie es erneut.', { type: 'error' });
                throw error;
            }
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            return format(date, 'dd.MM.yyyy HH:mm', { locale: de });
        },
    },
};
</script>

<style lang="scss">
.CommentWrap {
    position: relative;
    width: 100%;

    &.has-error {
        .CommentInput,
        .Label {
            border-color: var(--color-red) !important;
            color: var(--color-red) !important;
        }
    }

    &.is-vertical {
        .Label {
            position: relative;
            font-size: rem(14px);
            display: block;
        }
    }

    &.is-horizontal {
        .Label {
            font-size: rem(14px);
            display: block;

            &::after {
                content: ':';
            }
        }
    }

    &.is-disabled {
        .CommentInput {
            color: var(--color-text-gray-dark);
            background-color: var(--color-bg-disabled);
            cursor: not-allowed;
        }
    }

    .Label {
        font-weight: 600;
    }

    .CommentForm {
        margin-bottom: 15px;
        padding: 6px;
        background-color: var(--color-bg);
        border-radius: 8px;
        border: 0.5px solid var(--color-border);
    }

    .CommentInput {
        width: 100%;
        min-height: 80px;
        padding: 12px;
        border: 0.5px solid var(--color-border);
        border-radius: 5px;
        font-family: inherit;
        font-size: 1rem;
        background-color: var(--color-white);
        color: var(--color-text-black);
        resize: vertical;
        outline: none;
        transition: border-color 0.2s ease;

        &::placeholder {
            color: var(--color-text-gray-dark);
        }

        &:focus {
            border-color: var(--color-blue-dark);
            box-shadow: 0 0 0 2px rgba(var(--color-blue-dark), 0.1);
        }
    }

    .CommentActions {
        display: flex;
        gap: 10px;
        margin-top: 10px;
        justify-content: flex-end;
    }

    .AddCommentButton {
        display: flex;
        justify-content: flex-end;
        width: 100%;

        .Button {
            width: 100%;
        }
    }

    .CommentsList {
        .CommentItem {
            background-color: var(--color-bg);
            border: 0.5px solid var(--color-border);
            border-radius: 8px;
            padding: 10px;
            margin-bottom: 8px;
            transition: all 0.2s ease;

            &:hover {
                border-color: var(--color-blue-light);
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
            }

            &.is-editing {
                border-color: var(--color-blue-dark);
                background-color: rgba(var(--color-blue-light), 0.05);
            }
        }

        .CommentHeader {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 4px;
            padding-bottom: 4px;
            border-bottom: 0.5px solid var(--color-border);
        }

        .CommentDate {
            font-size: 0.75rem;
            color: var(--color-text-gray-dark);
        }

        .CommentControls {
            display: flex;
            gap: 4px;

            .Button i {
                font-size: 0.95rem;
            }
        }

        .CommentContent {
            color: var(--color-text-black);
            line-height: 1.35;
            white-space: pre-wrap;
            font-size: 0.85rem;
            word-wrap: break-word;
        }

        .CommentUpdated {
            font-size: 0.7rem;
            color: var(--color-text-gray-dark);
            margin-top: 6px;
            font-style: italic;
        }
    }

    .LoadingState {
        text-align: center;
        padding: 10px;
        color: var(--color-text-gray-dark);
        border-radius: 8px;
        margin-bottom: 15px;
    }

    .LoadingSpinner {
        font-style: italic;
    }

    .EmptyState {
        text-align: center;
        padding: 10px;
        color: var(--color-text-gray-dark);
        font-style: italic;
        border: 1px dashed var(--color-border);
        border-radius: 8px;
        margin-bottom: 15px;

        .EmptyStateText {
            font-size: 0.85rem;
        }
    }

    .Error {
        color: var(--color-red);
        margin-top: 5px;
        font-size: 12px;
        position: absolute;
        bottom: -23px;
        left: 12px;
    }

    @media screen and (max-width: $breakpoint-tablet-portrait) {
        .CommentActions {
            justify-content: stretch;

            .Button {
                flex: 1;
            }
        }
    }
}

.theme-dark {
    .CommentWrap {
        .CommentInput {
            background-color: var(--color-bg-dark);
            border-color: var(--color-border-dark);
            color: var(--color-text-light);

            &:focus {
                border-color: var(--color-blue-light);
            }
        }

        .CommentItem {
            background-color: var(--color-bg-dark);
            border-color: var(--color-border-dark);

            &:hover {
                border-color: var(--color-blue-light);
            }
        }

        .CommentForm {
            background-color: var(--color-bg-dark);
            border-color: var(--color-border-dark);
        }

        .EmptyState {
            border-color: var(--color-border-dark);
        }
    }
}
</style>
